import React from 'react';

import * as ManifestUI from '@project44/manifest-ds';
import { FetchError } from '@project44/ui-utils';

import { TApiDataState } from '../../../domain/interfaces';

export const useApiRequestWrapper = <T, K>(
  promiseFn: (...args: Array<T>) => Promise<K>,
  options?: {
    manifestToast?: {
      handleError?: (
        error: FetchError,
      ) => [
        message: string,
        options?: Omit<ManifestUI.ToastOptions, 'message'>,
      ];
      handleSuccess?: (
        data: K,
      ) => [
        message: string,
        options?: Omit<ManifestUI.ToastOptions, 'message'>,
      ];
    };
  },
): [
  TApiDataState<K>,
  (...promiseArguments: Array<T>) => Promise<K | undefined>,
] => {
  const [state, setState] = React.useState<TApiDataState<K>>({
    isLoading: false,
    data: undefined,
    error: undefined,
  });

  const functionCaller = async (
    ...promiseArguments: Array<T>
  ): Promise<K | undefined> => {
    try {
      setState({ error: undefined, data: undefined, isLoading: true });
      const response: K = await promiseFn.call(null, ...promiseArguments);

      setState({ error: undefined, isLoading: false, data: response });

      if (options?.manifestToast?.handleSuccess) {
        ManifestUI.toast.success(
          ...options.manifestToast.handleSuccess(response),
        );
      }
      return response;
    } catch (unknownException: unknown) {
      const error: FetchError =
        unknownException instanceof FetchError
          ? unknownException
          : new FetchError('Unknown Error', 500);

      setState({ data: undefined, error, isLoading: false });

      if (options?.manifestToast?.handleError) {
        ManifestUI.toast.error(...options.manifestToast.handleError(error));
      }
    }
  };

  React.useEffect(() => {
    return () => {
      setState({ error: undefined, data: undefined, isLoading: false });
    };
  }, []);

  return [state, functionCaller];
};
